import { useMemo } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import type { AccountInfo } from '@azure/msal-browser'
import {
  AuthError,
  STUB_TOKEN,
  createEntraAuthClient,
  createStubAuthClient,
  type AuthClient,
} from '@/lib/auth'

export type AuthState =
  | { kind: 'loading' }
  | {
      kind: 'authed'
      account: AccountInfo
      acquireToken: () => Promise<string>
      logout: () => Promise<void>
    }
  | { kind: 'unauth'; login: () => Promise<void> }

export function useStubAuth(): AuthState {
  return useMemo(() => {
    const client = createStubAuthClient()
    const account = client.getActiveAccount()
    if (!account) {
      return { kind: 'unauth', login: () => Promise.resolve() }
    }
    return {
      kind: 'authed',
      account,
      acquireToken: () => Promise.resolve(STUB_TOKEN),
      logout: () => Promise.resolve(),
    }
  }, [])
}

export function useEntraAuth(apiScope: string): AuthState {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  return useMemo<AuthState>(() => {
    if (inProgress !== 'none') return { kind: 'loading' }
    const client: AuthClient = createEntraAuthClient(instance, apiScope)
    const account = client.getActiveAccount()
    if (!isAuthenticated || !account) {
      return { kind: 'unauth', login: () => client.loginRedirect() }
    }
    return {
      kind: 'authed',
      account,
      acquireToken: async () => {
        try {
          return await client.acquireToken()
        } catch (err) {
          if (err instanceof AuthError) throw err
          throw new AuthError(
            err instanceof Error ? err.message : 'Token acquisition failed',
            'unknown',
          )
        }
      },
      logout: () => client.logoutLocal(),
    }
  }, [instance, inProgress, isAuthenticated, apiScope])
}
